import React, { useState, useEffect, useCallback } from "react";
import { connect } from "react-redux";
import {
  Col,
  Input,
  Radio,
  Row,
  Space,
  Spin,
  Switch,
  Empty,
  notification,
} from "antd";
import {
  SearchOutlined,
} from "@ant-design/icons";
import { ThemeButton, ThemeFilterBtn, ThemeModal, ThemeTag } from "../../pages/Styles/Theme";
import InitialsAvatar from "../InitialsAvatar/InitialsAvatar";
import {
  getShareableUsersApi,
  getShareableUsersReset,
} from "../../store/actions/userAction";
import {
  getProjectSharesApi,
  getProjectSharesReset,
  shareProjectApi,
  shareProjectReset,
  updateProjectShareApi,
  updateProjectShareReset,
  removeProjectShareApi,
  removeProjectShareReset,
} from "../../store/actions/projectAction";
import {
  getChatSharesApi,
  getChatSharesReset,
  shareChatApi,
  shareChatReset,
  updateChatShareApi,
  updateChatShareReset,
  removeChatShareApi,
  removeChatShareReset,
} from "../../store/actions/chatAction";
import debounce from "lodash/debounce";
import {
  PROJECT_SHARE_PRESETS,
  PROJECT_CUSTOM_OPTIONS,
  CHAT_SHARE_PRESETS,
  CHAT_CUSTOM_OPTIONS,
  getPermissionLabel,
} from "../../utils/sharePermissions";

const ShareModal = ({
  open,
  onClose,
  entityType = "project", // 'project' or 'chat'
  entityId,
  entityName,
  isSharedToMe = false, // If true, hide "Currently Shared With" section
  // Redux state - Users
  getShareableUsersState,
  getShareableUsers,
  getShareableUsersReset,
  // Redux state - Project shares
  getProjectSharesState,
  getProjectShares,
  getProjectSharesReset,
  shareProjectState,
  shareProject,
  shareProjectReset,
  updateProjectShareState,
  updateProjectShare,
  updateProjectShareReset,
  removeProjectShareState,
  removeProjectShare,
  removeProjectShareReset,
  // Redux state - Chat shares
  getChatSharesState,
  getChatShares,
  getChatSharesReset,
  shareChatState,
  shareChat,
  shareChatReset,
  updateChatShareState,
  updateChatShare,
  updateChatShareReset,
  removeChatShareState,
  removeChat,
  removeChatShareReset,
}) => {
  const [searchTerm, setSearchTerm] = useState("");
  const [users, setUsers] = useState([]);
  const [shares, setShares] = useState([]);
  const [canShare, setCanShare] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [permissionLevel, setPermissionLevel] = useState("view");
  const [customPermissions, setCustomPermissions] = useState({});
  const [view, setView] = useState("list"); // 'list' or 'permissions'

  const permissionLevels =
    entityType === "project" ? PROJECT_SHARE_PRESETS : CHAT_SHARE_PRESETS;
  const customPermissionsList =
    entityType === "project" ? PROJECT_CUSTOM_OPTIONS : CHAT_CUSTOM_OPTIONS;

  // Get current shares state based on entity type
  const currentSharesState =
    entityType === "project" ? getProjectSharesState : getChatSharesState;
  const currentShareState =
    entityType === "project" ? shareProjectState : shareChatState;
  const currentRemoveShareState =
    entityType === "project" ? removeProjectShareState : removeChatShareState;

  // Debounced search function
  const debouncedSearch = useCallback(
    debounce((term) => {
      getShareableUsers(term);
    }, 300),
    []
  );

  // Fetch shareable users on search term change
  useEffect(() => {
    if (open) {
      debouncedSearch(searchTerm);
    }
    return () => {
      debouncedSearch.cancel();
    };
  }, [searchTerm, open, debouncedSearch]);

  // Fetch current shares when modal opens
  useEffect(() => {
    if (open && entityId) {
      if (entityType === "project") {
        getProjectShares(entityId);
      } else {
        getChatShares(entityId);
      }
    }
    return () => {
      if (entityType === "project") {
        getProjectSharesReset();
      } else {
        getChatSharesReset();
      }
    };
  }, [open, entityId, entityType]);

  // Handle shareable users response
  useEffect(() => {
    if (getShareableUsersState.apiState === "success") {
      setUsers(getShareableUsersState.data?.data || []);
    }
  }, [getShareableUsersState]);

  // Handle shares response
  useEffect(() => {
    if (currentSharesState.apiState === "success") {
      setShares(currentSharesState.data?.data?.shares || []);
      setCanShare(currentSharesState.data?.data?.canShare || false);
    }
  }, [currentSharesState]);

  // Handle share success
  useEffect(() => {
    if (currentShareState.apiState === "success") {
      notification.success({
        message: "Shared Successfully",
        description: `${entityType === "project" ? "Library" : "Chat"} shared with ${selectedUser?.name}`,
      });
      setShares(currentShareState.data?.data?.shares || []);
      setSelectedUser(null);
      setView("list");
      setPermissionLevel("view");
      setCustomPermissions({});
      if (entityType === "project") {
        shareProjectReset();
      } else {
        shareChatReset();
      }
    } else if (currentShareState.apiState === "error") {
      notification.error({
        message: "Error",
        description: currentShareState.message || "Failed to share",
      });
      if (entityType === "project") {
        shareProjectReset();
      } else {
        shareChatReset();
      }
    }
  }, [currentShareState]);

  // Handle remove share success
  useEffect(() => {
    if (currentRemoveShareState.apiState === "success") {
      notification.success({
        message: "Share Removed",
        description: "User access has been removed",
      });
      setShares(currentRemoveShareState.data?.data?.shares || []);
      if (entityType === "project") {
        removeProjectShareReset();
      } else {
        removeChatShareReset();
      }
    } else if (currentRemoveShareState.apiState === "error") {
      notification.error({
        message: "Error",
        description: currentRemoveShareState.message || "Failed to remove share",
      });
      if (entityType === "project") {
        removeProjectShareReset();
      } else {
        removeChatShareReset();
      }
    }
  }, [currentRemoveShareState]);

  // Reset state when modal closes
  useEffect(() => {
    if (!open) {
      setSearchTerm("");
      setSelectedUser(null);
      setView("list");
      setPermissionLevel("view");
      setCustomPermissions({});
      getShareableUsersReset();
    }
  }, [open]);

  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
  };

  const handleSelectUser = (user) => {
    setSelectedUser(user);
    setView("permissions");
    setPermissionLevel("view");
    setCustomPermissions(permissionLevels.view.permissions);
  };

  const handlePermissionLevelChange = (level) => {
    setPermissionLevel(level);
    if (level !== "custom") {
      setCustomPermissions(permissionLevels[level].permissions);
    }
  };

  const handleCustomPermissionChange = (key, value) => {
    setCustomPermissions((prev) => ({
      ...prev,
      [key]: value,
    }));
    if (permissionLevel !== "custom") {
      setPermissionLevel("custom");
    }
  };

  const handleShare = () => {
    if (!selectedUser) return;

    const shareData = {
      userId: selectedUser._id,
      permissionLevel,
      customPermissions:
        permissionLevel === "custom" ? customPermissions : permissionLevels[permissionLevel].permissions,
    };

    if (entityType === "project") {
      shareProject(entityId, shareData);
    } else {
      shareChat(entityId, shareData);
    }
  };

  const handleRemoveShare = (userId) => {
    if (entityType === "project") {
      removeProjectShare(entityId, userId);
    } else {
      removeChat(entityId, userId);
    }
  };

  const handleBack = () => {
    setSelectedUser(null);
    setView("list");
    setPermissionLevel("view");
    setCustomPermissions({});
  };

  const isUserShared = (userId) => {
    return shares.some((share) => share.user?._id === userId);
  };

  const getSharedUser = (userId) => {
    return shares.find((share) => share.user?._id === userId);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
    });
  };

  const getSharePermissionLabel = (permissions) => {
    return getPermissionLabel(permissions, entityType);
  };

  const renderUserList = () => (
    <>
      <div style={{ marginBottom: 16 }}>
        <Input
          size="large"
          placeholder="Search by name or email..."
          prefix={<SearchOutlined style={{ color: "#999" }} />}
          value={searchTerm}
          onChange={handleSearchChange}
          allowClear
        />
      </div>

      {/* Currently Shared Section - Only show to owner/admin, not to shared users */}
      {shares.length > 0 && !isSharedToMe && (
        <div style={{ marginBottom: 24 }}>
          <div
            style={{
              fontSize: 12,
              fontWeight: 600,
              color: "#666",
              marginBottom: 8,
              textTransform: "uppercase",
            }}
          >
            Currently Shared With
          </div>
          <div
            style={{
              maxHeight: 200,
              overflowY: "auto",
              border: "1px solid #f0f0f0",
              borderRadius: 8,
            }}
          >
            {shares.map((share) => (
              <div
                key={share.user?._id}
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  padding: "12px 16px",
                  borderBottom: "1px solid #f0f0f0",
                }}
              >
                <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                  <InitialsAvatar name={share.user?.name} size={40} />
                  <div>
                    <div style={{ fontWeight: 500 }}>{share.user?.name}</div>
                    <div style={{ fontSize: 12, color: "#999" }}>
                      {share.user?.email}
                    </div>
                    <div style={{ fontSize: 11, color: "#999" }}>
                      {getSharePermissionLabel(share.permissions)} • Shared{" "}
                      {formatDate(share.shared_at)}
                    </div>
                  </div>
                </div>
                <ThemeButton
                  size="small"
                  danger
                  onClick={() => handleRemoveShare(share.user?._id)}
                  loading={currentRemoveShareState.apiState === "loading"}
                >
                  Remove
                </ThemeButton>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Available Users Section */}
      <div
        style={{
          fontSize: 12,
          fontWeight: 600,
          color: "#666",
          marginBottom: 8,
          textTransform: "uppercase",
        }}
      >
        {searchTerm ? "Search Results" : "Available Users"}
      </div>

      {getShareableUsersState.apiState === "loading" ? (
        <div style={{ textAlign: "center", padding: 40 }}>
          <Spin />
        </div>
      ) : users.length === 0 ? (
        <Empty
          description={
            searchTerm
              ? "No users found matching your search"
              : "No users available to share with"
          }
          style={{ padding: 40 }}
        />
      ) : (
        <div
          style={{
            maxHeight: 300,
            overflowY: "auto",
            border: "1px solid #f0f0f0",
            borderRadius: 8,
          }}
        >
          {users
            .filter((user) => !isUserShared(user._id))
            .map((user) => (
              <div
                key={user._id}
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  padding: "12px 16px",
                  borderBottom: "1px solid #f0f0f0",
                  cursor: "pointer",
                  transition: "background 0.2s",
                }}
                className="hover-bg"
                onClick={() => handleSelectUser(user)}
              >
                <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                  <InitialsAvatar name={user.name} size={40} />
                  <div>
                    <div style={{ fontWeight: 500 }}>{user.name}</div>
                    <div style={{ fontSize: 12, color: "#999" }}>
                      {user.email}
                    </div>
                    {user.organization?.name && (
                      <ThemeTag style={{ marginTop: 4, fontSize: 10 }}>
                        {user.organization.name}
                      </ThemeTag>
                    )}
                  </div>
                </div>
                <ThemeButton size="small" className="noBackground">
                  Share
                </ThemeButton>
              </div>
            ))}
        </div>
      )}
    </>
  );

  const renderPermissionSelection = () => (
    <>
      <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 16 }}>
        <ThemeFilterBtn
          className="shadow"
          onClick={handleBack}
          size="middle"
          icon={<img src="/images/back-icon.svg" alt="Back" />}
        />
        <InitialsAvatar name={selectedUser?.name} size={32} />
        <div>
          <div style={{ fontWeight: 600, fontSize: 14 }}>{selectedUser?.name}</div>
          <div style={{ fontSize: 12, color: "#666" }}>{selectedUser?.email}</div>
        </div>
      </div>

      <div style={{ marginBottom: 24 }}>
        <div
          style={{
            fontSize: 14,
            fontWeight: 600,
            marginBottom: 12,
          }}
        >
          Permission Level
        </div>
        <Radio.Group
          value={permissionLevel}
          onChange={(e) => handlePermissionLevelChange(e.target.value)}
          style={{ width: "100%" }}
        >
          <Space direction="vertical" size={8} style={{ width: "100%" }}>
            {Object.entries(permissionLevels).map(([key, config]) => (
              <div
                key={key}
                onClick={() => handlePermissionLevelChange(key)}
                style={{
                  width: "100%",
                  padding: "10px 12px",
                  border: `1px solid ${permissionLevel === key ? "#3d2969" : "#e8e8e8"}`,
                  borderRadius: 8,
                  background: permissionLevel === key ? "#f8f5ff" : "white",
                  cursor: "pointer",
                  display: "flex",
                  alignItems: "center",
                  gap: 10,
                }}
              >
                <Radio value={key} style={{ marginRight: 0 }} />
                <div>
                  <div style={{ fontWeight: 500, fontSize: 14 }}>{config.label}</div>
                  <div style={{ fontSize: 12, color: "#666" }}>{config.description}</div>
                </div>
              </div>
            ))}
          </Space>
        </Radio.Group>
      </div>

      {permissionLevel === "custom" && (
        <div style={{ marginBottom: 24 }}>
          <div
            style={{
              fontSize: 14,
              fontWeight: 600,
              marginBottom: 12,
            }}
          >
            Custom Permissions
          </div>
          <div
            style={{
              border: "1px solid #e8e8e8",
              borderRadius: 8,
              overflow: "hidden",
            }}
          >
            {customPermissionsList.map((permission, index) => (
              <div
                key={permission.key}
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  padding: "12px 16px",
                  borderBottom:
                    index < customPermissionsList.length - 1
                      ? "1px solid #f0f0f0"
                      : "none",
                }}
              >
                <span>{permission.label}</span>
                <Switch
                  size="small"
                  checked={customPermissions[permission.key] || false}
                  onChange={(checked) =>
                    handleCustomPermissionChange(permission.key, checked)
                  }
                />
              </div>
            ))}
          </div>
        </div>
      )}
    </>
  );

  return (
    <ThemeModal
      title={
        <>
          Share {entityType === "project" ? "Library" : "Chat"}
          <br />
          <span
            style={{
              fontSize: 13,
              fontWeight: 400,
              color: "#666",
            }}
          >
            {entityName}
          </span>
        </>
      }
      open={open}
      onCancel={onClose}
      centered
      width={520}
      footer={
        view === "permissions" ? (
          <div style={{ textAlign: "center", padding: "8px 0" }}>
            <Space>
              <ThemeButton
                size="large"
                className="noBackground"
                onClick={handleBack}
              >
                Cancel
              </ThemeButton>
              <ThemeButton
                size="large"
                onClick={handleShare}
                loading={currentShareState.apiState === "loading"}
              >
                Share
              </ThemeButton>
            </Space>
          </div>
        ) : null
      }
    >
      <style>
        {`
          .hover-bg:hover {
            background-color: #f9f9f9;
          }
          .ant-radio-wrapper .ant-radio-checked .ant-radio-inner {
            border-color: #3d2969 !important;
            background-color: #3d2969 !important;
          }
          .ant-radio-wrapper:hover .ant-radio-inner {
            border-color: #3d2969 !important;
          }
          .ant-switch.ant-switch-checked {
            background-color: #3d2969 !important;
          }
          .ant-switch.ant-switch-checked:hover {
            background-color: #5a3d8a !important;
          }
        `}
      </style>
      {view === "list" ? renderUserList() : renderPermissionSelection()}
    </ThemeModal>
  );
};

const mapStateToProps = (state) => ({
  getShareableUsersState: state.user.getShareableUsersState,
  getProjectSharesState: state.project.getProjectSharesState,
  shareProjectState: state.project.shareProjectState,
  updateProjectShareState: state.project.updateProjectShareState,
  removeProjectShareState: state.project.removeProjectShareState,
  getChatSharesState: state.chat.getChatSharesState,
  shareChatState: state.chat.shareChatState,
  updateChatShareState: state.chat.updateChatShareState,
  removeChatShareState: state.chat.removeChatShareState,
});

const mapDispatchToProps = (dispatch) => ({
  getShareableUsers: (search) => dispatch(getShareableUsersApi(search)),
  getShareableUsersReset: () => dispatch(getShareableUsersReset()),
  getProjectShares: (projectId) => dispatch(getProjectSharesApi(projectId)),
  getProjectSharesReset: () => dispatch(getProjectSharesReset()),
  shareProject: (projectId, data) => dispatch(shareProjectApi(projectId, data)),
  shareProjectReset: () => dispatch(shareProjectReset()),
  updateProjectShare: (projectId, userId, data) =>
    dispatch(updateProjectShareApi(projectId, userId, data)),
  updateProjectShareReset: () => dispatch(updateProjectShareReset()),
  removeProjectShare: (projectId, userId) =>
    dispatch(removeProjectShareApi(projectId, userId)),
  removeProjectShareReset: () => dispatch(removeProjectShareReset()),
  getChatShares: (chatId) => dispatch(getChatSharesApi(chatId)),
  getChatSharesReset: () => dispatch(getChatSharesReset()),
  shareChat: (chatId, data) => dispatch(shareChatApi(chatId, data)),
  shareChatReset: () => dispatch(shareChatReset()),
  updateChatShare: (chatId, userId, data) =>
    dispatch(updateChatShareApi(chatId, userId, data)),
  updateChatShareReset: () => dispatch(updateChatShareReset()),
  removeChat: (chatId, userId) => dispatch(removeChatShareApi(chatId, userId)),
  removeChatShareReset: () => dispatch(removeChatShareReset()),
});

export default connect(mapStateToProps, mapDispatchToProps)(ShareModal);
